<template>
  <TirPmPopover
    :custom-position="{ left: '-8.75rem', top: '0.25rem + $el:height' }"
    :is-disabled="isDisabled"
    @open="emit('onOpenPopover')"
    @close="emit('onClosePopover')"
  >
    <div class="task-time-tracker-popover__target">
      <span class="task-time-tracker-popover__label">Учет времени</span>
      <div :class="['task-time-tracker-popover__control', { 'is-disabled': isDisabled }]">
        <ClockIcon class="task-time-tracker-popover__control-icon" />
        <span class="task-time-tracker-popover__control-time">{{ timeStr }}</span>
      </div>
    </div>
    <template #content>
      <TirPmScrollbar :style="{ 'max-height': '24rem' }">
        <TaskTimeTracker
          ref="$timeTracker"
          :records="records"
          :timer-start-date="timerStartDate"
          :is-loading="isLoading"
          :isProjectAdmin="isProjectAdmin"
          @on-start="emit('onStart', $event)"
          @on-stop="emit('onStop')"
          @on-create-record="emit('onCreateRecord', $event)"
          @on-delete-record="emit('onDeleteRecord', $event)"
          @on-edit-record="emit('onEditRecord', $event)"
        />
      </TirPmScrollbar>
    </template>
  </TirPmPopover>
</template>

<script lang="ts" setup>
import { computed, onMounted, onUnmounted, PropType, ref } from "vue";
import { TirPmPopover, TirPmScrollbar } from "tir-components";
import { ClockIcon } from "tir-style-system/icons/outline";
import TaskTimeTracker from "./task-time-tracker.vue";
import { TaskTimeRecordModel, TaskTimeCreateRecordModel, TaskTimerEditRecordModel } from "./models";

const emit = defineEmits<{
  /**Событие открытия поповера */
  (e: "onOpenPopover"): void;
  /**Событие закрытия поповера */
  (e: "onClosePopover"): void;
  /**Событие старта таймера */
  (e: "onStart", isConfirm: boolean): void;
  /**Событие остановки таймера */
  (e: "onStop"): void;
  /**Событие создания новой записи таймера */
  (e: "onCreateRecord", value: TaskTimeCreateRecordModel): void;
  /**Удать запись таймера */
  (e: "onDeleteRecord", timerId: number): void;
  /**Событие редактирования записи */
  (e: "onEditRecord", value: TaskTimerEditRecordModel): void;
}>();

const props = defineProps({
  /**Заблокировать */
  isDisabled: {
    type: Boolean,
    default: false,
  },
  /**Записи */
  records: {
    type: Array as PropType<Array<TaskTimeRecordModel>>,
    default: [],
  },
  /**Дата старта таймера */
  timerStartDate: {
    type: Date,
  },
  /**Загрузка */
  isLoading: {
    type: Boolean,
    default: false,
  },
  /**Является ли пользователь админом проекта */
  isProjectAdmin: { type: Boolean },
});

const $timeTracker = ref<InstanceType<typeof TaskTimeTracker>>();

/**Текущая дата */
const currentDate = ref(new Date());

let intervalId: number;

/**Расчитать разницу */
const calculateTimeDifference = (
  date1: Date,
  date2: Date,
): { hours: number; minutes: number; seconds: number } => {
  // Получаем разницу в миллисекундах между двумя датами
  const diffInMs = Math.abs(date2.getTime() - date1.getTime());

  // Переводим разницу в часы, минуты и секунды
  const hours = Math.floor(diffInMs / (1000 * 60 * 60));
  const minutes = Math.floor((diffInMs % (1000 * 60 * 60)) / (1000 * 60));
  const seconds = Math.round((diffInMs % (1000 * 60)) / 1000);

  return { hours, minutes, seconds };
};

const formatNumber = (num: number) => {
  return num > 9 ? num.toString() : `0${num}`;
};

const getDiffTimeStr = (date1: Date, date2: Date) => {
  const { hours, minutes, seconds } = calculateTimeDifference(date1, date2);

  return `${formatNumber(hours)}:${formatNumber(minutes)}:${formatNumber(seconds)}`;
};

const timeStr = computed(() => {
  if (props.timerStartDate) return getDiffTimeStr(props.timerStartDate, currentDate.value);
  return "00:00:00";
});

onMounted(() => {
  intervalId = setInterval(() => {
    currentDate.value = new Date();
  }, 250);
});
onUnmounted(() => {
  if (intervalId) clearInterval(intervalId);
});

defineExpose({
  $timeTracker,
  clearInputValue: () => $timeTracker.value?.clearInputValue(),
});
</script>

<style lang="scss" scoped>
@import "./style/task-time-tracker-popover.style.scss";
</style>
